// dataHealthShared.tsx
//
// Small presentational + data helpers shared by the per-manufacturer Data Health
// section and the cross-manufacturer overview. Read-only: `useWatchBrandsMap`
// loads the brand list once for id → name / logo resolution; `BrandChip` renders
// a single brand pill in one of the audit tones. No writes.

import { useEffect, useState, type ReactNode, type SyntheticEvent } from 'react';
import toast from 'react-hot-toast';
import { getWatchBrands } from '../../services/firebase';
import type { WatchBrand } from '../../types';

/** Tone of a brand chip — encodes what the chip means in the audit, not just colour. */
export type BrandChipTone =
  | 'manual' // hand-curated allowlist
  | 'derived' // auto-derived from ref usage (dashed, to read as "computed")
  | 'new' // in derived, missing from manual — a promote candidate
  | 'stale' // in manual, missing from derived — possibly a dead link
  | 'excluded' // explicitly dismissed
  | 'primary'; // suggested / current primary brand

const TONE_CLASSES: Record<BrandChipTone, string> = {
  manual: 'bg-brand-50 border-brand-200 text-brand-800',
  derived: 'bg-sky-50 border-sky-300 border-dashed text-sky-800',
  new: 'bg-amber-50 border-amber-300 text-amber-900',
  stale: 'bg-rose-50 border-rose-300 text-rose-800',
  excluded: 'bg-gray-100 border-gray-300 text-gray-500 line-through',
  primary: 'bg-emerald-50 border-emerald-300 text-emerald-800',
};

/**
 * Load every watch brand once and expose an id → brand map (plus loading state).
 * Mirrors the existing BrandIdsManualIncludePicker load. Read-only.
 */
export function useWatchBrandsMap(): {
  brandsById: Map<string, WatchBrand>;
  loading: boolean;
} {
  const [brandsById, setBrandsById] = useState<Map<string, WatchBrand>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getWatchBrands()
      .then((all) => {
        if (cancelled) return;
        const m = new Map<string, WatchBrand>();
        for (const b of all) m.set(b.id, b);
        setBrandsById(m);
      })
      .catch((err) => {
        console.error('Failed to load brands for data-health', err);
        toast.error('Failed to load brands');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { brandsById, loading };
}

/** Human label for a brand id, falling back to the raw id when unresolved. */
export function brandLabel(
  brandsById: Map<string, WatchBrand>,
  id: string,
): string {
  const b = brandsById.get(id);
  return b ? b.displayName || b.name : id;
}

/**
 * A single brand pill in one of the audit tones. Read-only by default; pass
 * `onClick` to make it a click-target (adds cursor + hover:underline and stops
 * event propagation, so it works inside clickable table rows) — used to open the
 * brand drill-down while staying visually chip-like, not button-like.
 */
export function BrandChip({
  id,
  brandsById,
  tone,
  prefix,
  onClick,
}: {
  id: string;
  brandsById: Map<string, WatchBrand>;
  tone: BrandChipTone;
  /** Optional leading marker, e.g. a star for the primary chip. */
  prefix?: string;
  /** When set, the chip becomes an interactive click-target. */
  onClick?: () => void;
}) {
  const label = brandLabel(brandsById, id);
  const unresolved = !brandsById.has(id);
  const interactive = !!onClick;
  const baseTitle = unresolved ? `${id} (no matching brand doc)` : id;
  const handleActivate = (e: SyntheticEvent) => {
    e.stopPropagation();
    onClick?.();
  };
  return (
    <span
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? handleActivate : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleActivate(e);
              }
            }
          : undefined
      }
      title={interactive ? `${baseTitle} — show contributing refs` : baseTitle}
      className={`inline-flex items-center gap-1 border text-xs rounded-full px-2 py-0.5 ${
        TONE_CLASSES[tone]
      } ${interactive ? 'cursor-pointer hover:underline' : ''}`}
    >
      {prefix && <span aria-hidden>{prefix}</span>}
      <span>{label}</span>
      {unresolved && <span className="opacity-70">⚠</span>}
    </span>
  );
}

/**
 * Compact inline pill button used next to a brand chip for the Data Health quick
 * actions (promote / exclude / demote / restore / set-primary). Shared by the
 * per-manufacturer section and the cross-manufacturer overview so both surfaces
 * render identical controls. `stopPropagation` is baked in so the button works
 * inside a clickable table row without also triggering the row's click handler.
 */
export function ChipButton({
  children,
  onClick,
  title,
  disabled,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  tone?: 'neutral' | 'positive' | 'danger';
  /** Extra classes — e.g. hover-reveal utilities on the dense overview table. */
  className?: string;
}) {
  const toneClass =
    tone === 'danger'
      ? 'text-rose-700 border-rose-200 hover:bg-rose-100'
      : tone === 'positive'
        ? 'text-emerald-700 border-emerald-200 hover:bg-emerald-100'
        : 'text-gray-600 border-gray-200 hover:bg-gray-100';
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center rounded-full border bg-white px-1.5 py-0.5 text-[10px] font-medium disabled:opacity-40 disabled:cursor-not-allowed ${toneClass} ${
        className ?? ''
      }`}
    >
      {children}
    </button>
  );
}

/** Tones that carry a meaning worth explaining in the diff-chip legend. */
export type LegendTone = 'new' | 'stale' | 'excluded' | 'primary';

const LEGEND_META: Record<
  LegendTone,
  { sample: string; label: string; description: string }
> = {
  new: {
    sample: '+ Brand',
    label: 'new in derived',
    description:
      "New in derived — refs use this manufacturer's calibres for this brand, but it's not in the manual list.",
  },
  stale: {
    sample: '− Brand',
    label: 'stale in manual',
    description:
      'Stale in manual — this brand is manually linked but no refs actually use this manufacturer for it.',
  },
  excluded: {
    sample: 'Brand',
    label: 'excluded',
    description:
      'Excluded — explicitly dismissed via brandExclusions, removed from the effective set even if refs imply it.',
  },
  primary: {
    sample: '★ Brand',
    label: 'primary',
    description: 'Primary — the current or suggested primary brand for this manufacturer.',
  },
};

/**
 * Compact one-line legend for the audit chips. Each entry is a miniature chip in
 * its tone plus a short label, with the full explanation as a hover `title`. Pass
 * `items` to control which tones are shown (defaults to all four).
 */
export function ChipLegend({
  items = ['new', 'stale', 'excluded', 'primary'],
  className,
}: {
  items?: LegendTone[];
  className?: string;
}) {
  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gray-500 ${
        className ?? ''
      }`}
    >
      <span className="font-medium uppercase tracking-wide text-gray-400">Legend</span>
      {items.map((tone) => {
        const meta = LEGEND_META[tone];
        return (
          <span
            key={tone}
            className="inline-flex items-center gap-1"
            title={meta.description}
          >
            <span
              className={`inline-flex items-center rounded-full border px-1.5 py-0.5 ${TONE_CLASSES[tone]}`}
            >
              {meta.sample}
            </span>
            <span>{meta.label}</span>
          </span>
        );
      })}
    </div>
  );
}

/** Renders a wrapped row of chips, or a muted em-dash when empty. */
export function BrandChipRow({
  ids,
  brandsById,
  tone,
  emptyLabel = '—',
}: {
  ids: string[];
  brandsById: Map<string, WatchBrand>;
  tone: BrandChipTone;
  emptyLabel?: string;
}) {
  if (ids.length === 0) {
    return <span className="text-xs text-gray-400">{emptyLabel}</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {ids.map((id) => (
        <BrandChip key={id} id={id} brandsById={brandsById} tone={tone} />
      ))}
    </div>
  );
}
